/**
 * Lands a live `activity.created` socket event into the activity IDB
 * store + lets the sidebar re-derive its row count on the next pop.
 *
 * The legacy `addActivityMessage` worker handler still does the
 * write; this adapter just converts the v3 wire shape to the legacy
 * `ActivityMessageProps` shape it expects.
 *
 * `myself` is pulled out of localStorage (and, for the email, the private
 * profile store) rather than passed through
 * `channelService` because this fires from the socket router (no React
 * context) and the activity row's `receiver` field has always been the
 * signed-in user — there's no cross-tenant ambiguity to resolve.
 */

import { getPrivateProfile } from "../../../services/privateProfileStore";
import type { UserProps } from "../../../types/admin";
import { v3ActivityToLegacy } from "../adapters/v3ActivityToLegacy";
import { addActivityMessage } from "../components/sidebar/activity/services/addActivityMessage";

const EMPTY_USER: UserProps = {
    userId: "",
    userName: "",
    userEmail: "",
    teamId: "",
    teamName: "",
    avatarImgPath: "",
    tsLastSeen: "",
    tsJoined: "",
    isSystemUser: false,
};

function getMyself(): UserProps {
    return {
        ...EMPTY_USER,
        userId: localStorage.getItem("userId") || "",
        userName: localStorage.getItem("userName") || "",
        // Memory only (MIP-619).
        userEmail: getPrivateProfile().userEmail,
        teamId: localStorage.getItem("teamId") || "",
        teamName: localStorage.getItem("teamName") || "",
        avatarImgPath: localStorage.getItem("avatarImgPath") || "",
    };
}

/** Event name dispatched on `window` after a v3 activity has landed
 *  in IDB. `useChatManagement` listens for this and calls
 *  `funcSetActivityMessages()` so the sidebar React state re-derives
 *  without waiting for the next mount / chat switch. The legacy WS
 *  handler used a direct `useCM.funcSetActivityMessages()` call from
 *  inside the handler — we can't do that from the socketRouter
 *  (no React context), hence the global event bus.
 */
export const V3_ACTIVITY_CREATED_EVENT = "v3:activity:created";

export async function handleV3Activity(payload: unknown): Promise<void> {
    try {
        const myself = getMyself();
        const legacy = v3ActivityToLegacy(
            payload as Parameters<typeof v3ActivityToLegacy>[0],
            myself
        );

        // `activity.created` is routed to the recipient's `user:{id}`
        // room, which is per-USER — so someone in two teams receives the
        // other team's entries while this team is on screen. Ignore them
        // rather than storing them: nothing is lost, because switching
        // teams wipes this store and re-syncs the new team's feed from
        // the server. A row with no `teamId` comes from a backend that
        // predates the field and is kept.
        if (legacy.teamId && myself.teamId && legacy.teamId !== myself.teamId) {
            return;
        }

        await addActivityMessage(legacy);

        // `detail.activity` carries the adapted row so listeners that
        // need the data (web-notification router, future bridges)
        // don't have to re-fetch from IDB. The useChatManagement
        // listener doesn't read it — it always re-derives from IDB.
        window.dispatchEvent(
            new CustomEvent(V3_ACTIVITY_CREATED_EVENT, { detail: { activity: legacy } })
        );
    } catch (e) {
        console.error("[handleV3Activity] failed", e);
    }
}
